import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, ActionButton } from '@common/components';
import {
  EditIcon,
  ViewIcon,
  PublishIcon,
  UnpublishIcon,
  Delete2Icon,
} from '@common/components/icons';
import { ActionsCell } from '@common/components/Table/Cells';
import { Tooltip } from '@common/components/Tooltip';
import StatusBadge from '../StatusBadge';
import SavedReportsListModal from '../SavedReportsListModal/SavedReportsListModal';
import ScheduledReportsListModal from '../ScheduledReportsListModal/ScheduledReportsListModal';
// eslint-disable-next-line import/no-unresolved
import * as styles from './styles.module.css';
import * as managementStyles from '../ReportsTableView/styles.module.css';

// Chart Icon Component
function ChartIcon() {
  return <div className={styles.iconContainer}>
    <svg
      className={styles.icon}
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 18.8889H7.77778V12.2222H10V18.8889ZM14.4444 18.8889H12.2222V7.77778H14.4444V18.8889ZM18.8889 18.8889H16.6667V14.4444H18.8889V18.8889ZM21.1111 21.1111H5.55556V5.55556H21.1111V21.2222M21.1111 3.33333H5.55556C4.33333 3.33333 3.33333 4.33333 3.33333 5.55556V21.1111C3.33333 22.3333 4.33333 23.3333 5.55556 23.3333H21.1111C22.3333 23.3333 23.3333 22.3333 23.3333 21.1111V5.55556C23.3333 4.33333 22.3333 3.33333 21.1111 3.33333Z"
        fill="currentColor"
      />
    </svg>
  </div>
}

function ReportCard({
  report,
  onView,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  isAdmin,
}) {
  const { t } = useTranslation();
  const [showSavedReportsModal, setShowSavedReportsModal] = useState(false);
  const [showScheduledReportsModal, setShowScheduledReportsModal] = useState(false);

  // System default reports (seeded predefined reports) are read-only:
  // they cannot be edited, renamed or deleted.
  const reportIsDefault = Boolean(report?.isDefault);

  const handleViewSavedReports = useCallback((e) => {
    e.stopPropagation(); // Prevent card click
    setShowSavedReportsModal(true);
  }, []);

  const handleCloseSavedReportsModal = useCallback(() => {
    setShowSavedReportsModal(false);
  }, []);

  const handleViewScheduledReports = useCallback((e) => {
    e.stopPropagation(); // Prevent card click
    setShowScheduledReportsModal(true);
  }, []);

  const handleCloseScheduledReportsModal = useCallback(() => {
    setShowScheduledReportsModal(false);
  }, []);

  // Handle card click to view report
  const handleCardClick = useCallback(() => {
    if (onView) {
      onView(report);
    }
  }, [onView, report]);

  // Create actions menu similar to table view
  const actionsMenu = useMemo(() => [
    {
      key: 'view',
      component: (
        <ActionButton
          icon={ViewIcon}
          text={t('reports.actions.view')}
          className={managementStyles['action-button']}
        />
      ),
      onClick: (row) => {
        onView?.(row.original);
      },
    },
    {
      key: 'edit',
      component: (
        <ActionButton
          icon={EditIcon}
          text={t('reports.actions.edit')}
          className={managementStyles['action-button']}
          disabled={reportIsDefault}
          title={reportIsDefault ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: reportIsDefault,
      onClick: (row) => {
        onEdit?.(row.original);
      },
      hidden: () => !isAdmin,
    },
    {
      key: 'publish',
      component: (
        <ActionButton
          icon={PublishIcon}
          text={t('reports.actions.publish')}
          className={managementStyles['action-button']}
          disabled={reportIsDefault}
          title={reportIsDefault ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: reportIsDefault,
      onClick: (row) => {
        onPublish?.(row.original);
      },
      hidden: (row) => {
        if (!isAdmin) return true;
        const status = row.original?.status;
        return status !== 0 && status !== '0';
      },
    },
    {
      key: 'unpublish',
      component: (
        <ActionButton
          icon={UnpublishIcon}
          text={t('reports.actions.unpublish')}
          className={managementStyles['action-button']}
          disabled={reportIsDefault}
          title={reportIsDefault ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: reportIsDefault,
      onClick: (row) => {
        onUnpublish?.(row.original);
      },
      hidden: (row) => {
        if (!isAdmin) return true;
        const status = row.original?.status;
        return status !== 1 && status !== '1';
      },
    },
    {
      key: 'delete',
      component: (
        <ActionButton
          icon={Delete2Icon}
          text={t('reports.actions.delete')}
          className={`${managementStyles['action-button']} ${managementStyles['delete-action']}`}
          disabled={reportIsDefault}
          title={reportIsDefault ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: reportIsDefault,
      onClick: (row) => {
        onDelete?.(row.original);
      },
      hidden: () => !isAdmin,
    },
  ], [t, onView, onEdit, onPublish, onUnpublish, onDelete, isAdmin, reportIsDefault]);

  // Create a mock row object for ActionsCell
  const mockRow = useMemo(() => ({
    original: report,
  }), [report]);

  // Actions component - wrap in div to stop propagation
  const actions = isAdmin ? (
    <div 
      className={styles.menuContainer}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Prevent card keyboard activation when interacting with menu
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
      role="presentation"
    >
      <ActionsCell row={mockRow} actionsMenu={actionsMenu} />
    </div>
  ) : null;

  // Footer component with saved reports button (and scheduled for admin users)
  const footer = (
    <div className={styles.footerButtons}>
      <button
        type="button"
        className={styles.viewSavedButton}
        onClick={handleViewSavedReports}
      >
        <svg
          className={styles.folderIcon}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.75 2.5C1.33579 2.5 1 2.83579 1 3.25V12.75C1 13.1642 1.33579 13.5 1.75 13.5H14.25C14.6642 13.5 15 13.1642 15 12.75V5.25C15 4.83579 14.6642 4.5 14.25 4.5H7.81066L6.56066 3.25H1.75ZM0 3.25C0 2.2835 0.783502 1.5 1.75 1.5H6.75C6.88261 1.5 7.00979 1.55268 7.10355 1.64645L8.60355 3.14645C8.69732 3.24021 8.75 3.36739 8.75 3.5V4H14.25C15.2165 4 16 4.7835 16 5.25V12.75C16 13.7165 15.2165 14.5 14.25 14.5H1.75C0.783502 14.5 0 13.7165 0 12.75V3.25Z"
            fill="currentColor"
          />
        </svg>
        <span>{t('savedReports.title')}</span>
      </button>
      {isAdmin && (
        <button
          type="button"
          className={styles.viewScheduledButton}
          onClick={handleViewScheduledReports}
        >
          <svg
            className={styles.clockIcon}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.33333 8C1.33333 4.31799 4.31799 1.33333 8 1.33333C11.682 1.33333 14.6667 4.31799 14.6667 8C14.6667 11.682 11.682 14.6667 8 14.6667C4.31799 14.6667 1.33333 11.682 1.33333 8Z"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="square"
            />
            <path
              d="M8 4.33333V8L10 10"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="square"
            />
          </svg>
          <span>{t('scheduledReports.scheduled')}</span>
        </button>
      )}
    </div>
  );

  return (
    <>
      <Card
        title={report.name}
        icon={<ChartIcon />}
        badge={isAdmin ? <StatusBadge status={report.status} /> : null}
        actions={actions}
        footer={footer}
        onClick={handleCardClick}
        status={String(report.status)}
        variant="elevated"
        className={styles.card}
        iconClassName={styles.iconContainer}
        titleClassName={styles.title}
        contentClassName={styles.description}
        dataAttributes={{ 'data-report-id': report.id }}
      >
        <Tooltip
          content={report.description}
          position="bottom"
          delay={100}
          className={styles.descriptionTooltipWrapper}
        >
          <p className={styles.description}>
            {report.description || t('reports.noDescription')}
          </p>
        </Tooltip>
      </Card>

      <SavedReportsListModal
        isOpen={showSavedReportsModal}
        onClose={handleCloseSavedReportsModal}
        dashboardId={report.id}
        dashboardName={report.name}
      />

      <ScheduledReportsListModal
        isOpen={showScheduledReportsModal}
        onClose={handleCloseScheduledReportsModal}
        dashboardId={report.id}
        dashboardName={report.name}
      />
    </>
  );
}

ReportCard.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isDefault: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  }).isRequired,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onPublish: PropTypes.func,
  onUnpublish: PropTypes.func,
  onDelete: PropTypes.func,
  isAdmin: PropTypes.bool,
};

ReportCard.defaultProps = {
  onView: null,
  onEdit: null,
  onPublish: null,
  onUnpublish: null,
  onDelete: null,
  isAdmin: false,
};

export default React.memo(ReportCard);
